import { ApiError, AUTH_ERROR_CODES } from '@getslot/api-client'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { session } from '../api/session'
import { getInitData } from '../telegram/web-app'

export type AuthStatus = 'loading' | 'guest' | 'beta_closed' | 'unavailable' | 'ready'

interface AuthValue {
  status: AuthStatus
  retry: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

async function signIn(): Promise<AuthStatus> {
  const initData = getInitData()
  if (initData === null) return 'guest'
  try {
    await session.loginWithTelegram(initData)
    return 'ready'
  } catch (error) {
    if (error instanceof ApiError && error.code === AUTH_ERROR_CODES.betaClosed) return 'beta_closed'
    if (error instanceof ApiError && !error.isUnavailable && error.status < 500) return 'guest'
    return 'unavailable'
  }
}

/**
 * Вход при запуске: в Mini App по initData, на сайте — экран приветствия со ссылкой в бота
 * (вход на сайте кодом из бота появится вместе с ботом уведомлений).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const queryClient = useQueryClient()

  const retry = useCallback(() => {
    setStatus('loading')
    void signIn().then(setStatus)
  }, [])

  useEffect(() => {
    void signIn().then(setStatus)
    return session.onExpired(() => {
      queryClient.clear()
      setStatus('guest')
    })
  }, [queryClient])

  const value = useMemo(() => ({ status, retry }), [status, retry])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (value === null) throw new Error('useAuth вне AuthProvider')
  return value
}
